/**
 * Ad-click attribution capture (Google Ads pilot foundation).
 *
 * tm_attribution — localStorage JSON holding the last-touch ad-click params
 * ({ gclid, utmSource, utmMedium, utmCampaign, capturedAt }) captured from the
 * URL query string on ANY route. Sent as the optional `attribution` field on
 * the signup payload, written once onto the User row, and consumed (cleared)
 * after a successful registration.
 *
 * Rules (per the ad-attribution ticket — intentional, don't "fix"):
 * - Trigger: a non-empty gclid OR utm_source in the URL. On trigger the stored
 *   object is overwritten ENTIRELY (last-touch wins). That means a
 *   utm_source-only visit (e.g. a UTM-tagged email link) erases a previously
 *   stored gclid — this is the ticket's rule.
 * - A visit with no attribution params leaves the stored object untouched.
 * - Staleness: on read at signup, entries older than ATTRIBUTION_TTL_DAYS are
 *   treated as absent and the key is cleared (Google only matches offline
 *   conversions to clicks within 90 days).
 * - referredByUsername (trade-map share loop, Asana 1218807104530338): the
 *   `via` query param (TRADE_MAP_REFERRAL_PARAM) is captured into the SAME
 *   object under the same last-touch and TTL rules, and alone is enough to
 *   trigger a capture. It is deliberately not `ref`: that name feeds the tm_ref
 *   affiliate cookie and outranks utm_source in referral_source. Kept to the
 *   username charset and 64 characters here, so a mangled or hostile link can
 *   never make the backend's DTO validation reject a registration.
 */

import { TRADE_MAP_REFERRAL_PARAM } from './tradeMapShare';

export const ATTRIBUTION_STORAGE_KEY = 'tm_attribution';
/** Usernames are alphanumerics, underscore, hyphen and space (inputSanitization.sanitizeUsername) */
const REFERRED_BY_PATTERN = /^[a-z0-9 _-]{1,64}$/;
export const ATTRIBUTION_TTL_DAYS = 90;

const ATTRIBUTION_TTL_MS = ATTRIBUTION_TTL_DAYS * 24 * 60 * 60 * 1000;

export interface StoredAttribution {
  gclid?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  /** The sharer whose trade-map link this visitor landed on; lowercase */
  referredByUsername?: string;
  capturedAt: string; // ISO 8601
}

/** Normalize a query param: trim, and treat empty/whitespace-only as absent. */
const cleanParam = (value: string | null): string | undefined => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
};

/** Trim, lowercase, and keep only a value that could be a username; anything else is absent. */
const cleanReferredBy = (value: string | null): string | undefined => {
  const lowered = value?.trim().toLowerCase();
  return lowered && REFERRED_BY_PATTERN.test(lowered) ? lowered : undefined;
};

/**
 * Capture attribution params from a location.search string. No-ops (leaving
 * any stored attribution intact) unless a non-empty gclid, utm_source or `via`
 * is present; on trigger, overwrites the stored object entirely (last-touch).
 */
export const captureAttribution = (search: string): void => {
  try {
    const params = new URLSearchParams(search);
    const gclid = cleanParam(params.get('gclid'));
    const utmSource = cleanParam(params.get('utm_source'));
    const referredByUsername = cleanReferredBy(params.get(TRADE_MAP_REFERRAL_PARAM));
    if (!gclid && !utmSource && !referredByUsername) return;

    const attribution: StoredAttribution = {
      gclid,
      utmSource,
      utmMedium: cleanParam(params.get('utm_medium')),
      utmCampaign: cleanParam(params.get('utm_campaign')),
      referredByUsername,
      capturedAt: new Date().toISOString(),
    };
    // JSON.stringify drops undefined fields, so absent params are omitted
    localStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(attribution));
  } catch {
    // localStorage not available
  }
};

/**
 * Read stored attribution for inclusion in the signup payload. Returns null
 * (and clears the key) when the entry is missing, malformed, or older than
 * ATTRIBUTION_TTL_DAYS.
 */
export const getAttributionForSignup = (): StoredAttribution | null => {
  try {
    const raw = localStorage.getItem(ATTRIBUTION_STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    const capturedAtMs = Date.parse(parsed?.capturedAt);
    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      Number.isNaN(capturedAtMs) ||
      Date.now() - capturedAtMs > ATTRIBUTION_TTL_MS
    ) {
      clearAttribution();
      return null;
    }
    return parsed as StoredAttribution;
  } catch {
    // localStorage unavailable or JSON malformed
    clearAttribution();
    return null;
  }
};

/** Remove stored attribution (consumed after a successful registration). */
export const clearAttribution = (): void => {
  try {
    localStorage.removeItem(ATTRIBUTION_STORAGE_KEY);
  } catch {
    // localStorage not available
  }
};
